import React, { FC } from 'react'
import Link from 'next/link'
import { useSelector } from 'react-redux'
import NoSSR from 'react-no-ssr'
import LazyImage from '@components/Image'

import { getSiteLanguage, findOpeningTime } from '@utils'
import { logger, validateAxiosResponse, Translation, verifyTranslateValue } from '@dmi-mch/utils'
import { useTranslatedLabel } from '@hooks'
import { Text, Separator, MediaPlaceholder } from '@components'

import { Artworks as ArtworksService } from '@services'
import { AddToCollectionContext } from '@components/collections/AddToCollection'
import { ButtonIcon, Container, Row, Col, SharingButton, Tag, Button } from '@mch-group/uikit-components'
import { useLabelsFromAPI } from '@dmi-mch/hooks'
import EventDetailInfos from './EventDetailInfos'
import EventDetailButtons from './EventDetailButtons'
import Styles from './EventDetail.styles'

const EventDetail: FC<EventDetailProps> = ({ data, showSocialButtons, showActionButtons,
  labels = {} as any, ...props }) => {
  const { shared } = useSelector((state) => state)

  // @ts-ignore
  const globalLabels = shared.globalModules.labelModule?.labels
  const openingTime = props.openingTime || data.openingDateTimes ? findOpeningTime(data.openingDateTimes, null) : null
  const { showAddToCollectionModal } = React.useContext(AddToCollectionContext)
  const { labels: apiLabels = {} } = useLabelsFromAPI(
    ['AltTags', 'OnlineShowRoom', 'EventsAndExhibitionsForm', 'EventsAndExhibitions'],
    getSiteLanguage()
  )
  const { AltTags, OnlineShowRoom, EventsAndExhibitionsForm, EventsAndExhibitions } = apiLabels

  const language = getSiteLanguage()
  const { translatedFields } = useTranslatedLabel('Event', data.id, language)
  // @ts-ignore
  const translatedContent = translatedFields && new Translation(translatedFields, language, data.id?.toString())
  const [eventData, setEventData] = React.useState<any>(null)
  const fetchArtWork = React.useCallback(
    async (didUnmount = false) => {
      try {
        let req = null
        // @ts-ignore
        req = await ArtworksService.getExhibitionDetailArtworks({
          eventId: data?.eventId ? data?.eventId : data.id,
          includeEventArtworks: true,
          includeArtworksNotInShows: true,
          sortBy: 'position',
          offset: 0,
          limit: 50,
          locale: getSiteLanguage()
        })
        if (!didUnmount && validateAxiosResponse(req)) {
          // @ts-ignore
          setEventData(req.data.items[0])
        }
      } catch (err) {
        !didUnmount && logger(err)
      }
    },
    [data.eventId, data.id]
  )

  React.useEffect(() => {
    let didUnmount = false
    !eventData && fetchArtWork(didUnmount)
    return () => {
      didUnmount = true
    }
  }, [fetchArtWork, eventData])

  const title = translatedContent?.translate('title').startsWith('##')
    ? data.title
    : translatedContent?.translate('title')
  const trasnlatedTitle = translatedContent?.translate('title') ? title : data.title

  const shortParagraphText = translatedContent?.translate('shortParagraphText').startsWith('##')
    ? data.shortParagraphText
    : translatedContent?.translate('shortParagraphText')
  const translatedShortParagraphText = translatedContent?.translate('shortParagraphText')
    ? shortParagraphText
    : data.shortParagraphText

  const imageCaption = translatedContent?.translate('imageCaption').startsWith('##')
    ? data.imageCaption
    : translatedContent?.translate('imageCaption')
  const translatedImageCaption = translatedContent?.translate('imageCaption') ? imageCaption : data.imageCaption

  const longParagraphText = translatedContent?.translate('longParagraphText').startsWith('##')
    ? data.longParagraphText
    : translatedContent?.translate('longParagraphText')
  const translatedLongParagraphText = translatedContent?.translate('longParagraphText')
    ? longParagraphText
    : data.longParagraphText

  const openingHoursDate =
    !verifyTranslateValue(translatedContent?.translate('openingHours')) && language !== 'en'
      ? translatedContent?.translate('openingHours')
      : data.exhibitionOpeningHours
  const translatedOpeningHours =
    translatedContent?.translate('openingHours') && translatedContent?.translate('openingHours') !== ''
      ? openingHoursDate
      : data.exhibitionOpeningHours
  return (
    <Styles>
      <Container className='px-5 px-lg-8 px-md-7 mt-0'>
        <Row>
          <Col md={10} xs={12} className='mb-8'>
            <header>
              {!!((data.accountData && data.accountData.name) || data.venue) && (
                <h1 className='h4'>
                  {(data.accountData && data.accountData.name) || data.venue.name}
                  {data.virtual
                    ? `, ${EventsAndExhibitions?.onlineLocationLabel}`
                    : data.venue && !!data.venue.city && `, ${data.venue.city}`}
                </h1>
              )}
              {data.title && (
                <h2 className='text-paragraph-1 pt-3'>
                  <b>{language === 'en' ? data.title : trasnlatedTitle}</b>
                </h2>
              )}
              {data.shortParagraphText && (
                <h5>{language === 'en' ? data.shortParagraphText : translatedShortParagraphText}</h5>
              )}
            </header>
          </Col>
          <Col xs={12} md={2}>
            {showSocialButtons && (
              <div className='d-flex justify-content-md-end mt-md-7 mb-8'>
                <div className='me-3'>
                  <NoSSR>
                    <SharingButton
                      title={AltTags?.shareRoom}
                      media={['facebook', 'whatsapp', 'linkedin', 'twitter', 'weebo', 'wechat', 'mail', 'link-default']}
                      variant='outline'
                    />
                  </NoSSR>
                </div>
                <ButtonIcon
                  title={AltTags?.addToCollection}
                  icon='collections-add'
                  variant='outline'
                  // @ts-ignore
                  onClick={() =>
                    showAddToCollectionModal(
                      data.id,
                      data?.exhibition ? '4b' : '4a',
                      undefined,
                      undefined,
                      data?.openingDateTimes[0]?.id
                    )
                  }
                />
              </div>
            )}
          </Col>
        </Row>

        <Row>
          <Col md={8} xs={12} className='event-detail__image-wrapper'>
            {data.eventImage && (
              <>
                <MediaPlaceholder aspectRatio='16:10' className='border-radius-standard'>
                  <LazyImage
                    quality='auto:best'
                    publicId={data.eventImage}
                    crop='fill'
                    aspect_ratio='16:10'
                    alt={data && data.title}
                    responsive
                    preload='lqip'
                  />
                </MediaPlaceholder>
                <Text className='event-detail__image-caption mt-8' isSmall isSoftText>
                  {language === 'en' ? data.imageCaption : translatedImageCaption}
                </Text>
              </>
            )}
            {data.longParagraphText && (
              <Text className='event-detail__description' isHtml fixNewLines>
                {language === 'en' ? data.longParagraphText : translatedLongParagraphText}
              </Text>
            )}
          </Col>

          <Col md={4} xs={12}>
            <EventDetailInfos
              data={data}
              globalLabels={globalLabels}
              organizedByLabel={labels.organizedBy}
              recommendedByLabel={labels.recommendedBy}
              openingTime={openingTime}
              OnlineStatus={OnlineShowRoom?.onlineVenueLabel}
              onlineLocationLabel={EventsAndExhibitions?.onlineLocationLabel}
              translatedExhibitionOpeningHours={language !== 'en' ? translatedOpeningHours : ''}
              language={language}
              additionalInformationLabel={EventsAndExhibitionsForm?.additionalInformationLabel}
            />

            {data.rsvprequired && (
              <div className='mt-9 mb-8'>
                <Text forwardedAs='p'>
                  <strong>{labels.rsvp}</strong>
                </Text>
                <Text forwardedAs='p' isSmall>
                  {props.rsvpText}
                </Text>
                {props.rsvpForm}
              </div>
            )}

            {data.ownerAccountId && EventsAndExhibitions?.Disclaimer && (
              <Text className='events-exhibitions__disclamer'>{EventsAndExhibitions?.Disclaimer}</Text>
            )}
            {showActionButtons && (
              <div>
                <EventDetailButtons
                  data={data}
                  setToFavLabel={labels.setToFav}
                  exportToCalLabel={labels.exportToCal}
                  //    @ts-ignore
                  openingTime={openingTime}
                />
              </div>
            )}

            {['VIP', 'PRIVATE_INVITATION'].includes(data.accessPermission) ||
            (data.eventLabels && data.eventLabels.length) ||
            (data.eventTypes && data.eventTypes.length) ? (
              <div>
                {data.accessPermission === 'VIP' && (
                  <Tag className='boxed-text inverse' label={globalLabels.las_grp1_vip || 'VIP'} />
                )}
                {data.accessPermission === 'PRIVATE_INVITATION' && (
                  <Tag
                    className='boxed-text inverse'
                    label={globalLabels.las_grp1_private_invitation || 'Private Invitation'}
                  />
                )}
                {data.eventLabels &&
                  data.eventLabels.length > 0 &&
                  data.eventLabels.map((label) => <Tag className='boxed-text' key={label.id} label={label.title} />)}
                {data.eventTypes &&
                  data.eventTypes.length > 0 &&
                  data.eventTypes.map((eventType) => (
                    <Tag className='boxed-text' key={eventType.id} label={eventType.type} />
                  ))}
              </div>
            ) : null}

            {data.onlineShowId && eventData && (
              <>
                <br />
                <Separator />
                <div>
                  <Link
                    href={{ pathname: '/' }}
                    as={`/catalog/artwork/${eventData.id}/${eventData.cleanTitle}`}
                    legacyBehavior
                  >
                    <Button icon='eye-view' iconAlign='left' size='compact' type='button' variant='primary'>
                      {data.enterRoomButtonContent}
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </Col>
        </Row>
      </Container>
    </Styles>
  )
}

interface EventDetailProps {
  data: any;
  pagePath?: string;
  openingTime?: any;
  showActionButtons?: boolean;
  showSocialButtons?: boolean;
  rsvpText?: string;
  rsvpForm?: React.ReactNode;
  labels?: {
    rsvp?: string;
    organizedBy?: string;
    recommendedBy?: string;
    setToFav?: string;
    exportToCal?: string;
  };
  globalLabels?: {
    las_grp1_onwards?: string;
    las_grp1_vip?: string;
    las_grp1_private_invitation?: string;
  };
  ownerAccountId?: any;
}

export default EventDetail
